<template>
    <div class="Organizations">
        <q-card
            class="OrganizationsCard"
            bordered
        >
            <q-card-section class="OrganizationsTabs">
                <div class="flex row-stretch-center">
                    <q-tabs
                        v-model="groupOwnerType"
                        no-caps
                        inline-label
                        align="left"
                        active-color="primary"
                        indicator-color="primary"
                        @update:model-value="handleChageGroupOwnerType({ value: $event })"
                    >
                        <q-tab
                            v-for="option in groupOwnerTypes"
                            :key="option.value"
                            :name="option.value"
                            :label="option.label"
                            :icon="option.icon"
                        />
                    </q-tabs>
                </div>
            </q-card-section>

            <q-separator />

            <q-card-section class="OrganizationsBody">
                <OrganizationsLists
                    ref="table"
                    :rows="organizations"
                    :total="total"
                    :search="filters.search"
                    :loading="isFetching"
                    :pagination="pagination"
                    @on-new-item="handleNewItem"
                    @on-delete="handleDeleteItem"
                    @on-update="handleUpdateItem"
                    @on-click="handleClickItem"
                    @on-fetch-data="fetchData"
                />
            </q-card-section>
        </q-card>
        <ModalRequest
            ref="delPrDialog"
            :title="$t('organizations.delete')"
            :text="$t('organizations.modals.delete.text', { name: (selected || {}).name })"
            :loading="isLoading"
            @on-accept="deleteOrganization"
        />
        <ModalNameDesc
            ref="editPrDialog"
            withoutDesc
            :title="$t('organizations.edit')"
            :action="$t('actions.save')"
            :loading="isLoading"
            @on-submit="updateOrganization"
        />
        <ModalNameDesc
            ref="newPrDialog"
            withoutDesc
            :title="$t('organizations.new')"
            :action="$t('actions.create')"
            :loading="isLoading"
            @on-submit="createOrganization"
        />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, notifyError, notifySuccess,
} from '@/api'
import { ModalRequest, ModalNameDesc } from '@/components'
import { dialogMixin } from '@/mixins'
import { OrganizationsLists } from './sections'

const STORAGE_KEY = 'organizationsState'

export default {
    name: 'OrganizationsView',
    components: {
        ModalRequest,
        ModalNameDesc,
        OrganizationsLists,
    },
    mixins: [dialogMixin],
    props: {
        filters: VueTypes.shape({
            search: VueTypes.string.def(''),
            archived: VueTypes.bool.def(false),
        }),
    },
    data() {
        const stored = this.getStoredState()
        return {
            organizations: undefined,
            total: undefined,
            isFetching: false,
            isLoading: false,
            showAllOrganizations: true,
            groupOwnerTypes: [
                { label: this.$t('organizations.createdByMe'), value: 'createdByMe', icon: 'person' },
                { label: this.$t('organizations.sharedWithMe'), value: 'sharedWithMe', icon: 'group' },
            ],
            groupOwnerType: stored.groupOwnerType,
            pagination: stored.paginations[stored.groupOwnerType] || {},
            header: {
                search: '',
                archived: false,
            },
        }
    },
    computed: {
        params() {
            return {
                ...this.filters,
                ...this.pagination,
                shared: this.groupOwnerType !== 'createdByMe',
                onlyActive: this.onlyActive,
            }
        },
    },
    watch: {
        filters(value) {
            if (value) this.fetchData()
        },
    },
    mounted() {
        // Robustez al leer sortDirection y sortBy
        try {
            const sortDir = localStorage.getItem('organizationsListSortDirection')
            if (!sortDir || !['ASCENDING', 'DESCENDING'].includes(sortDir)) {
                localStorage.setItem('organizationsListSortDirection', 'ASCENDING')
            }
        } catch (e) {
            localStorage.setItem('organizationsListSortDirection', 'ASCENDING')
        }
        try {
            const sortBy = localStorage.getItem('organizationsListSortBy')
            if (!sortBy || !['NAME', 'DATE', 'OWNER'].includes(sortBy)) {
                localStorage.setItem('organizationsListSortBy', 'NAME')
            }
        } catch (e) {
            localStorage.setItem('organizationsListSortBy', 'NAME')
        }

        // Robustez al leer paginación
        let stored
        try {
            stored = this.getStoredState()
        } catch (e) {
            stored = {
                groupOwnerType: 'createdByMe',
                paginations: {
                    createdByMe: {},
                    sharedWithMe: {},
                },
            }
            this.setStoredState(stored)
        }
        if (stored.paginations[this.groupOwnerType] && Object.keys(stored.paginations[this.groupOwnerType]).length) {
            this.pagination = stored.paginations[this.groupOwnerType]
        } else {
            this.pagination = { page: 1, rowsPerPage: 25 }
            stored.paginations[this.groupOwnerType] = this.pagination
            this.setStoredState(stored)
        }
        this.fetchData()
    },

    methods: {
        getStoredState() {
            try {
                const stored = localStorage.getItem(STORAGE_KEY)
                return stored
                    ? JSON.parse(stored)
                    : {
                        groupOwnerType: 'createdByMe',
                        paginations: {
                            createdByMe: {},
                            sharedWithMe: {},
                        },
                    }
            } catch (e) {
                return {
                    groupOwnerType: 'createdByMe',
                    paginations: {
                        createdByMe: {},
                        sharedWithMe: {},
                    },
                }
            }
        },
        setStoredState(state) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
            } catch (e) {
                // Silenciar error de escritura
            }
        },
        savePagination() {
            const stored = this.getStoredState()
            stored.paginations[this.groupOwnerType] = this.pagination
            this.setStoredState(stored)
        },
        handleChageGroupOwnerType({ value }) {
            this.groupOwnerType = value
            const stored = this.getStoredState()
            stored.groupOwnerType = value
            this.setStoredState(stored)
            this.pagination = stored.paginations[value] || {}
            this.fetchData()
        },
        fetchData(newPagination) {
            if (newPagination) {
                this.pagination = { ...this.pagination, ...newPagination }
                this.savePagination()
            }

            const formattedParams = this.formatParams()
            this.isFetching = true
            apiRequest(Api().organizations.list(formattedParams))
                .then(({ data, total }) => {
                    this.organizations = data
                    this.total = total
                })
                .catch(notifyError)
                .finally(() => { this.isFetching = false })
        },
        formatParams() {
            const {
                onlyActive, archived, search, shared,
            } = this.params
            const { page, rowsPerPage } = this.pagination
            const sortDirection = localStorage.getItem('organizationsListSortDirection')
            const sortBy = localStorage.getItem('organizationsListSortBy')

            return {
                shared,
                onlyActive,
                search: search || null,
                includeArchived: !!archived,
                sortDirection,
                sortBy,
                ...(rowsPerPage ? { start: page - 1 } : {}),
                ...(rowsPerPage ? { limit: rowsPerPage } : {}),
            }
        },

        async createOrganization({ name }) {
            this.isLoading = true
            apiRequest(Api().organizations.create({ name }))
                .then(() => {
                    notifySuccess(this.$t('organizations.notifications.created.text'))
                    this.fetchData()
                    this.$refs.newPrDialog.close()
                })
                .catch((error) => {
                    notifyError(error)
                })
                .finally(() => { this.isLoading = false })
        },
        async updateOrganization({ uuid, name }) {
            this.isLoading = true
            apiRequest(Api().organizations.update({ organizationUid: uuid, params: { name } }))
                .then(() => {
                    notifySuccess(this.$t('organizations.notifications.updated.text'))
                    this.fetchData()
                    this.$refs.editPrDialog.close()
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        async deleteOrganization() {
            const { uuid } = this.selected
            this.isLoading = true
            apiRequest(Api().organizations.delete({ organizationUid: uuid }))
                .then(() => {
                    notifySuccess(this.$t('organizations.notifications.deleted.text'))
                    this.fetchData()
                    this.$refs.delPrDialog.close()
                    this.selected = undefined
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        handleClickItem(organization) {
            const { uuid } = organization
            this.$router.push({ name: 'Organization', params: { organizationId: uuid } })
        },
    },
}
</script>

<style scoped lang="scss">
.OrganizationsCard {
    background: $white;
}

.OrganizationsTabs {
    padding: 0 16px;

    :deep(.q-tab__label) {
        letter-spacing: 1.25px;
    }
}

.OrganizationsBody {
    padding: 16px;
}
</style>
